import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid } from '@mui/material';
import { useSnackbar } from '../../context/SnackbarContext';
import {
  useCampaignContextController,
  setCampaignId
} from '../../context/CampaignContext';
import LoadingComponent from '../../components/loading';
import {
  getCampaigns,
  createCampaign,
  duplicateCampaign,
  archiveCampaign,
  Campaign,
  CampaignProspectType
} from '../../api/campaigns';
import CampaignTypeModal from '../../components/modal/CampaignTypeModal';
import WebTopbar from '../../components/topbar/WebTopbar';
import CampaignHeader from './components/CampaignHeader';
import CampaignCard from './components/CampaignCard';
import EmptyState from './components/EmptyState';

const INITIAL_OFFSET = 0;
const INITIAL_LIMIT = 50;
const DEFAULT_CAMPAIGN_DESCRIPTION = 'Placeholder description';

const CampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [, dispatch] = useCampaignContextController();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);

  useEffect(() => {
    setCampaignId(dispatch, null);
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    getCampaigns(INITIAL_OFFSET, INITIAL_LIMIT, false)
      .then((response) => {
        setCampaigns(response.campaigns || []);
        setTotalCount(response.total_count || 0);
      })
      .catch((error) => {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to load campaigns';
        showSnackbar(errorMessage, 'error');
        setCampaigns([]);
        setTotalCount(0);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleCreateCampaign = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCampaignTypeSelect = useCallback(
    async (prospectType: CampaignProspectType) => {
      setIsCreatingCampaign(true);
      setIsModalOpen(false);

      const campaignName =
        prospectType === CampaignProspectType.ACCOUNT
          ? 'Accounts Campaign'
          : 'Opportunities Campaign';

      try {
        const campaign = await createCampaign({
          name: campaignName,
          description: DEFAULT_CAMPAIGN_DESCRIPTION,
          prospect_type: prospectType
        });
        navigate(`/campaign/${campaign.id}`);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to create campaign';
        showSnackbar(errorMessage, 'error');
      } finally {
        setIsCreatingCampaign(false);
      }
    },
    []
  );

  const handleDuplicateCampaign = useCallback(
    async (campaign: Campaign) => {
      try {
        const newCampaign = await duplicateCampaign(campaign.id);
        setCampaigns((prev) => [newCampaign, ...prev]);
        setTotalCount((prev) => prev + 1);
        showSnackbar('Campaign duplicated successfully', 'success');
        navigate(`/campaign/${newCampaign.id}`);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to duplicate campaign';
        showSnackbar(errorMessage, 'error');
      }
    },
    [navigate, showSnackbar]
  );

  const handleArchive = useCallback(
    async (campaign: Campaign, archived: boolean) => {
      try {
        await archiveCampaign(campaign.id, archived);
        setCampaigns((prev) => prev.filter((c) => c.id !== campaign.id));
        setTotalCount((prev) => Math.max(0, prev - 1));
        showSnackbar('Campaign archived', 'success');
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to archive campaign';
        showSnackbar(errorMessage, 'error');
      }
    },
    [showSnackbar]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Note: PrivateRoute already handles authentication, so this check is redundant
  // but kept for additional safety. However, we should avoid navigating during render.
  // If user is not loaded and not loading, PrivateRoute will handle the redirect.

  if (isCreatingCampaign) {
    return <LoadingComponent message="Creating campaign..." />;
  }

  if (loading) {
    return <LoadingComponent message="Loading campaigns..." />;
  }

  return (
    <>
      <WebTopbar />

      <Box
        sx={{
          width: '100%',
          height: '100%',
          padding: '24px 48px',
          paddingBottom: '8px',
          overflow: 'auto',
          boxSizing: 'border-box',
          backgroundColor: (theme) => theme.customColors.themeSecondary
        }}
      >
        <CampaignHeader
          totalCount={totalCount}
          onCreateClick={handleCreateCampaign}
          showArchivedLink
        />

        {campaigns.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid container spacing={3}>
            {campaigns.map((campaign) => (
              <Grid item xs={12} sm={6} md={4} key={campaign.id}>
                <CampaignCard
                  campaign={campaign}
                  onDuplicate={handleDuplicateCampaign}
                  onArchive={handleArchive}
                />
              </Grid>
            ))}
          </Grid>
        )}

        <CampaignTypeModal
          open={isModalOpen}
          onClose={handleModalClose}
          onSelect={handleCampaignTypeSelect}
        />
      </Box>
    </>
  );
};

export default CampaignPage;
